import { useCallback, useRef } from 'react';
import type { IntervalsOAuthTokenData } from '../../../shared/types';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../services/api';
import { logger } from '../utils/logger';

interface UseOAuthPopupResult {
  initiateOAuth: () => Promise<void>;
  isLoading: boolean;
}

/**
 * Custom hook for handling OAuth popup flow
 *
 * Manages the entire OAuth process:
 * 1. Opens popup window with OAuth URL
 * 2. Listens for postMessage from popup
 * 3. Processes token and updates auth state
 * 4. Handles errors and cleanup
 */
export const useOAuthPopup = (): UseOAuthPopupResult => {
  const { state, checkAuth } = useAuth();
  const popupRef = useRef<Window | null>(null);
  const messageListenerRef = useRef<((event: MessageEvent) => void) | null>(null);
  const storageListenerRef = useRef<((event: StorageEvent) => void) | null>(null);
  const authCompleteRef = useRef<boolean>(false);

  // Define error handler first (no dependencies)
  const handleOAuthError = useCallback((error: string | Error | unknown) => {
    let errorMessage: string;
    if (typeof error === 'string') {
      errorMessage = error;
    } else if (error instanceof Error) {
      errorMessage = error.message;
    } else if (error && typeof error === 'object') {
      errorMessage = JSON.stringify(error);
    } else {
      errorMessage = 'Unknown error';
    }
    logger.error('OAuth error', { error: errorMessage });
  }, []);

  // Define success handler (depends on checkAuth)
  const handleOAuthSuccess = useCallback(
    async (tokenData: IntervalsOAuthTokenData) => {
      try {
        logger.info('OAuth success, refreshing auth state...');

        // Backend is the authoritative source for token storage
        // We only need to trigger a refresh of the auth context to pick up the new connection
        // The token is already stored in MongoDB by the OAuth callback handler

        // Trigger auth context to refresh and pick up the new token from backend
        await checkAuth();

        logger.info('Auth state refreshed after OAuth success');

        // Dispatch event for other components to react to connection
        window.dispatchEvent(new CustomEvent('intervals-connected', { detail: tokenData }));

        logger.info('intervals-connected event dispatched');
      } catch (error) {
        logger.error('Failed to process OAuth success', { error });
        handleOAuthError('Failed to process authentication token');
      }
    },
    [checkAuth, handleOAuthError]
  );

  // Cleanup function
  const cleanup = useCallback(() => {
    // Remove message listener
    if (messageListenerRef.current) {
      window.removeEventListener('message', messageListenerRef.current);
      messageListenerRef.current = null;
    }

    // Remove storage listener
    if (storageListenerRef.current) {
      window.removeEventListener('storage', storageListenerRef.current);
      storageListenerRef.current = null;
    }

    // Close popup if still open
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.close();
    }
    popupRef.current = null;
    authCompleteRef.current = false;
  }, []);

  const initiateOAuth = useCallback(async (): Promise<void> => {
    if (state.isLoading) {
      return;
    }

    try {
      // Clean up any existing popup/listeners
      cleanup();

      // Use the new token-based link endpoint
      const authResponse = await apiClient.linkIntervalsAccount();

      if (!authResponse.success) {
        throw new Error(authResponse.error || 'Failed to get authorization URL');
      }

      // Check if user already has a valid token
      if (authResponse.data?.hasValidToken) {
        // User already has a valid token, just refresh auth state
        logger.info('User already has valid token, refreshing auth state');
        authCompleteRef.current = true; // Mark as complete to prevent "cancelled by user" error
        await checkAuth();
        // Dispatch event so ProfileModal updates
        window.dispatchEvent(
          new CustomEvent('intervals-connected', { detail: { alreadyConnected: true } })
        );
        return;
      }

      // No valid token exists, proceed with OAuth flow
      if (!authResponse.data?.authUrl) {
        throw new Error('No authorization URL provided');
      }

      // Calculate popup window position (centered)
      const width = 500;
      const height = 600;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      // Open popup window
      popupRef.current = window.open(
        authResponse.data.authUrl,
        'oauth_popup',
        `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`
      );

      if (!popupRef.current) {
        throw new Error('Failed to open popup window. Please allow popups for this site.');
      }

      // Clear any stale OAuth result from storage
      localStorage.removeItem('intervals_oauth_result');

      // Set up storage listener for cross-window communication
      // This works even when window.opener is lost after OAuth redirect
      const handleStorageEvent = (event: StorageEvent) => {
        if (event.key === 'intervals_oauth_result' && event.newValue) {
          try {
            const result = JSON.parse(event.newValue);
            // Only process recent results (within last 30 seconds)
            if (Date.now() - result.timestamp < 30000) {
              if (result.type === 'success' && result.tokenData) {
                authCompleteRef.current = true;
                handleOAuthSuccess(result.tokenData);
                // Clean up the storage key
                localStorage.removeItem('intervals_oauth_result');
                cleanup();
              } else if (result.type === 'error') {
                authCompleteRef.current = true;
                handleOAuthError(result.error || 'Authentication failed');
                localStorage.removeItem('intervals_oauth_result');
                cleanup();
              }
            }
          } catch (e) {
            logger.error('Failed to parse OAuth result from storage', { error: e });
          }
        }
      };

      storageListenerRef.current = handleStorageEvent;
      window.addEventListener('storage', handleStorageEvent);

      // Set up message listener for popup communication
      const handleMessage = (event: MessageEvent) => {
        // Verify origin for security
        if (event.origin !== window.location.origin) {
          return;
        }

        const { type, tokenData, error } = event.data;

        if (type === 'OAUTH_CALLBACK_LOADED') {
          // Don't cleanup for callback loaded - let the popup continue processing
          return;
        } else if (type === 'OAUTH_SUCCESS' && tokenData) {
          authCompleteRef.current = true;
          handleOAuthSuccess(tokenData);
          // Cleanup after handling success
          cleanup();
        } else if (type === 'OAUTH_ERROR') {
          authCompleteRef.current = true;
          handleOAuthError(error || 'Authentication failed');
          // Cleanup after handling error
          cleanup();
        }
      };

      messageListenerRef.current = handleMessage;
      window.addEventListener('message', handleMessage);

      // Monitor popup closure and check for OAuth result in localStorage
      // The storage event only fires for changes from other windows,
      // so we also poll localStorage as a fallback
      const checkClosed = setInterval(() => {
        // Check if OAuth completed via localStorage (fallback for lost window.opener)
        const oauthResult = localStorage.getItem('intervals_oauth_result');
        if (oauthResult && !authCompleteRef.current) {
          try {
            const result = JSON.parse(oauthResult);
            // Only process recent results (within last 30 seconds)
            if (Date.now() - result.timestamp < 30000) {
              if (result.type === 'success' && result.tokenData) {
                authCompleteRef.current = true;
                clearInterval(checkClosed);
                handleOAuthSuccess(result.tokenData);
                localStorage.removeItem('intervals_oauth_result');
                cleanup();
                return;
              }
            }
          } catch (e) {
            // Ignore parse errors
          }
        }

        if (popupRef.current?.closed) {
          clearInterval(checkClosed);

          // Only treat as "cancelled by user" if auth wasn't completed successfully
          if (messageListenerRef.current && !authCompleteRef.current) {
            // Don't log error if user just closed popup - this is expected behavior
            logger.info('OAuth popup closed by user');
            cleanup();
          } else {
            cleanup();
          }
        }
      }, 500);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to start authentication';
      handleOAuthError(message);
      cleanup();
    }
  }, [state.isLoading, cleanup, checkAuth, handleOAuthSuccess, handleOAuthError]);

  return {
    initiateOAuth,
    isLoading: state.isLoading,
  };
};
